import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// API
import {
  getAllCompanyModulesAsSuperAdmin,
  updateCompanyModuleAsSuperAdmin,
  updateMultipleCompanyModulesAsSuperAdmin,
  impersonateCEOAsSuperAdmin,
} from "services/api"; // Adjust path
import { IconButton } from "@mui/material";

// List of all possible paid modules in your system
const ALL_POSSIBLE_MODULES = [
  { key: "Policies", label: "Policies" },
  { key: "Performance", label: "Performance Management" },
  { key: "Competencies", label: "Competencies" },
  { key: "Trainings", label: "Training & Development" },
  { key: "Succession Plan", label: "Succession Planning" },
  { key: "WLA", label: "Workload Analysis" },
  { key: "Talent Acquisition", label: "Talent Acquisition (ATS)" },
];

// Map of modules that must trigger each other
const LINKED_MODULES = {
  Competencies: "Trainings",
  Trainings: "Competencies",
};

//"Strategic Info" "Organization" "Policies" "Performance" "Organization" "Competencies"  "Trainings" "Succession Plan"  "Talent Acquisition" "WLA"

function ManageCompany() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // If user accesses directly without state, redirect back
  useEffect(() => {
    if (!state?.company) navigate("/super-admin");
  }, [state, navigate]);

  const company = state?.company || {};

  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [activeModules, setActiveModules] = useState({});
  const [isAllEnabled, setIsAllEnabled] = useState(false);

  // 1. Fetch Modules on Load
  useEffect(() => {
    if (!company.tenant) return;

    const fetchModules = async () => {
      const data = await getAllCompanyModulesAsSuperAdmin(company.tenant);

      // Convert list of objects to a simple map: { "Payroll": true, "WLA": true }
      const moduleMap = {};
      let allFound = false;

      if (Array.isArray(data)) {
        data.forEach((m) => {
          if (m.key === "ALL") allFound = true;
          moduleMap[m.key] = true; // If it's in the DB, it's active
        });
      }

      setIsAllEnabled(allFound);
      setActiveModules(moduleMap);
      setLoading(false);
    };

    fetchModules();
  }, [company]);

  // 2. Handle Toggle (Individual or Linked)
  const handleToggle = async (key) => {
    const oldState = activeModules[key];
    const newState = !oldState;

    // Check if this module is linked to another
    const linkedKey = LINKED_MODULES[key];

    if (linkedKey) {
      // === LINKED MODULE LOGIC (Bulk Update) ===

      // 1. Optimistic UI Update: Set BOTH to the new state
      setActiveModules((prev) => ({
        ...prev,
        [key]: newState,
        [linkedKey]: newState,
      }));

      // 2. Call Bulk API
      // We send a map of { "Competencies": true, "Trainings": true }
      await updateMultipleCompanyModulesAsSuperAdmin(company.tenant, {
        [key]: newState,
        [linkedKey]: newState,
      });
    } else {
      // === STANDARD LOGIC (Single Update) ===

      setActiveModules((prev) => ({ ...prev, [key]: newState }));
      await updateCompanyModuleAsSuperAdmin(company.tenant, key, newState);
    }

    // Common Logic: If we disabled something while "ALL" was active, turn off "ALL" visual
    if (isAllEnabled && !newState) setIsAllEnabled(false);
  };

  // 3. Handle "ALL" Toggle
  const handleAllToggle = async () => {
    const newState = !isAllEnabled;
    setIsAllEnabled(newState);

    // Visual update
    if (newState) {
      // If turning ALL on, visually turn everything on
      const allTrue = {};
      ALL_POSSIBLE_MODULES.forEach((m) => (allTrue[m.key] = true));
      setActiveModules(allTrue);
    } else {
      // If turning ALL off, we theoretically clear everything or keep current?
      // Let's keep current state but just remove the "ALL" flag backend side
      setActiveModules({});
    }

    await updateCompanyModuleAsSuperAdmin(company.tenant, "ALL", newState);
  };

  // 4. Ghost Mode Login
  const handleImpersonate = async () => {
    setImpersonating(true);
    const data = await impersonateCEOAsSuperAdmin(company.tenant);
    if (data && data.token) {
      // 1. Clear current session
      const oldToken = localStorage.getItem("authToken");

      // 2. Set new session
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("tenant", data.tenantId);
      // Flag to show "Switch back to Super Admin" button later if you want
      localStorage.setItem("isSuperAdminSession", "true");
      localStorage.setItem("originalToken", oldToken); // Store old token to switch back later
      // 3. Force reload/redirect to dashboard
      window.location.href = "/dashboard";
    } else {
      alert("Failed to impersonate. No CEO/Admin found for this company.");
      setImpersonating(false);
    }
  };

  const renderLogo = (logoBytes) => {
    if (logoBytes) return `data:image/jpeg;base64,${logoBytes}`;
    return null;
  };

  if (!company.tenant) return null;

  return (
    <MDBox py={3}>
      {/* HEADER CARD */}
      <Card sx={{ mb: 3, overflow: "visible" }}>
        <MDBox
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          {/* Left Side: Back Button + Logo + Info */}
          <MDBox display="flex" alignItems="center">
            {/* Back Button */}
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 3 }}>
              <Icon fontSize="medium">arrow_back</Icon>
            </IconButton>

            {/* New Professional Logo Container */}
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="5rem" // Larger size
              height="5rem" // Square container
              bgColor="white"
              borderRadius="lg"
              shadow="sm"
              p={1} // Padding inside the box so logo doesn't touch edges
              mr={3}
              sx={{ border: "1px solid #dee2e6" }} // Subtle border definition
            >
              {company.companyLogo ? (
                <MDBox
                  component="img"
                  src={renderLogo(company.companyLogo)}
                  alt={company.companyName}
                  width="100%"
                  height="100%"
                  sx={{ objectFit: "contain" }} // Ensures logo isn't stretched or cropped
                />
              ) : (
                <Icon fontSize="large" color="secondary" sx={{ fontSize: "40px !important" }}>
                  business
                </Icon>
              )}
            </MDBox>

            {/* Company Details */}
            <MDBox display="flex" flexDirection="column" justifyContent="center">
              <MDTypography
                variant="h4"
                fontWeight="bold"
                textTransform="capitalize"
                color="dark"
                lineHeight={1.2}
              >
                {company.companyName}
              </MDTypography>

              <MDBox display="flex" alignItems="center" mt={0.5} opacity={0.7}>
                <Icon fontSize="small" sx={{ mr: 0.5, fontSize: "16px !important" }}>
                  vpn_key
                </Icon>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  {company.tenant}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Right Side: Action Button */}
          <MDBox mt={{ xs: 3, md: 0 }}>
            <MDButton
              variant="gradient"
              color="dark" // "Dark" looks more professional for admin actions than "Info"
              size="medium"
              sx={{ minWidth: "180px" }}
              startIcon={
                impersonating ? <CircularProgress size={16} color="inherit" /> : <Icon>login</Icon>
              }
              onClick={handleImpersonate}
              disabled={impersonating}
            >
              {impersonating ? "Accessing..." : "Impersonate Admin"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      <Grid container spacing={3}>
        {/* MODULE CONTROL */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium">
                  Module Configuration
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Control paid features
                </MDTypography>
              </MDBox>

              <MDBox
                display="flex"
                alignItems="center"
                bgcolor="#f0f2f5"
                borderRadius="8px"
                px={2}
                py={0.5}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  color={isAllEnabled ? "success" : "text"}
                >
                  FULL ACCESS (ALL)
                </MDTypography>
                <Switch checked={isAllEnabled} onChange={handleAllToggle} color="success" />
              </MDBox>
            </MDBox>

            <Divider />

            <MDBox p={2}>
              {loading ? (
                <MDBox textAlign="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <Grid container spacing={2}>
                  {/* CORE FREE MODULES (Read Only) */}
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <b>Core Modules:</b> Strategic Info & Organization are always active.
                    </Alert>
                  </Grid>

                  {/* PAID MODULES */}
                  {ALL_POSSIBLE_MODULES.map((mod) => (
                    <Grid item xs={12} sm={6} key={mod.key}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        border="1px solid #eee"
                        borderRadius="lg"
                        p={2}
                      >
                        <MDBox>
                          <MDTypography variant="subtitle2" fontWeight="bold">
                            {mod.label}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {mod.key}
                          </MDTypography>
                        </MDBox>
                        <Switch
                          checked={isAllEnabled || !!activeModules[mod.key]}
                          onChange={() => handleToggle(mod.key)}
                          disabled={isAllEnabled} // Disable individual switches if ALL is on
                          color="info"
                        />
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
              )}
            </MDBox>
          </Card>
        </Grid>

        {/* SUPPORT & DIAGNOSTICS PANEL */}
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Support & Diagnostics
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Monitor system health and issues
              </MDTypography>
            </MDBox>

            <MDBox p={2} pt={0}>
              {/* Status Rows */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  Database Connection
                </MDTypography>
                <MDBox display="flex" alignItems="center">
                  <MDBox
                    sx={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      bgcolor: "#4CAF50",
                      mr: 0.5,
                    }}
                  />
                  <MDTypography variant="caption" fontWeight="bold" color="success">
                    Connected
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  System Language
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold" color="dark">
                  {company.language || "English (En)"}
                </MDTypography>
              </MDBox>

              <Divider />

              {/* Action Buttons */}
              <MDBox mt={2} display="flex" flexDirection="column" gap={2}>
                {/* 1. Error Logs Button */}
                <MDButton
                  variant="gradient"
                  color="error"
                  fullWidth
                  startIcon={<Icon>error_outline</Icon>}
                  onClick={() => navigate("/super-admin/logs", { state: { company } })}
                >
                  View Error Logs
                </MDButton>

                {/* 2. Tickets Placeholder */}
                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  startIcon={<Icon>confirmation_number</Icon>}
                  onClick={() => navigate("/super-admin/tickets", { state: { company } })}
                >
                  Support Tickets (0)
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default ManageCompany;
